/**
 * What a category looks like on the map: a disc of its colour with its emoji on it. MapLibre's
 * glyph fonts have no emoji, so a pin cannot be text; each category's pin is painted here, once,
 * on a canvas, and handed to the map as an image (`map.addImage("cat-<id>", …)`).
 */
import type { PoiCategory } from "./pois.ts";

/** The colours offered when a category is added or edited, in the order they are offered. */
export const CATEGORY_PALETTE = [
  "#f97316",
  "#ef4444",
  "#ec4899",
  "#8b5cf6",
  "#3b82f6",
  "#06b6d4",
  "#22c55e",
  "#eab308",
  "#64748b",
] as const;

/** Each palette colour's name, which is how a colour is told apart by ear. */
export const COLOUR_NAMES: Record<(typeof CATEGORY_PALETTE)[number], string> = {
  "#f97316": "Orange",
  "#ef4444": "Red",
  "#ec4899": "Pink",
  "#8b5cf6": "Purple",
  "#3b82f6": "Blue",
  "#06b6d4": "Cyan",
  "#22c55e": "Green",
  "#eab308": "Yellow",
  "#64748b": "Slate",
};

/** A place with no category: a grey pin, and only ever under "All". */
export const UNCATEGORISED = { emoji: "📍", color: "#94a3b8" } as const;

/**
 * A category's name as a sentence says it: first letter up, the rest as typed. The seeded ones are
 * stored in lower case ("sports"), which reads as a slip in "Remove ⚽ sports?" or on a badge.
 */
export function categoryName(name: string): string {
  const [first = "", ...rest] = name;
  return first.toLocaleUpperCase() + rest.join("");
}

/** The emoji a category gets when nobody has chosen one: the plain pin. */
export const DEFAULT_EMOJI = "📍";

/** The first palette colour no category wears yet, so a new one stands apart; the first when all do. */
export function nextColour(categories: Pick<PoiCategory, "color">[]): string {
  const worn = new Set(categories.map((c) => c.color));
  return CATEGORY_PALETTE.find((c) => !worn.has(c)) ?? CATEGORY_PALETTE[0];
}

/** The screen's pixels per CSS pixel, so a pin is painted sharp; 1 where it cannot be read. */
export const iconRatio = () =>
  typeof window !== "undefined" && window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;

/** What `addImage` takes: `ImageData`, or its shape where there is no canvas to make one. */
export type IconImage = { width: number; height: number; data: Uint8ClampedArray };

/**
 * One category's pin, `size` CSS pixels across, painted at `ratio` device pixels per CSS pixel
 * (the map is told the same ratio, so it shows `size`). A white ring keeps the disc readable on
 * any basemap and over the heat.
 *
 * Where there is no 2D canvas (jsdom, without a canvas package) the image is a transparent one of
 * the right size, so the map still has a pin by that name; a test that wants to see what is
 * painted stubs `HTMLCanvasElement.prototype.getContext`.
 */
export function categoryIcon(
  emoji: string,
  color: string,
  size: number,
  ratio: number = iconRatio(),
): IconImage {
  const px = Math.round(size * ratio);
  const canvas = document.createElement("canvas");
  canvas.width = px;
  canvas.height = px;
  let ctx: CanvasRenderingContext2D | null;
  try {
    ctx = canvas.getContext("2d");
  } catch {
    ctx = null;
  }
  if (!ctx) return { width: px, height: px, data: new Uint8ClampedArray(px * px * 4) };
  const r = px / 2;
  const ring = Math.max(1, Math.round(2 * ratio));
  ctx.beginPath();
  ctx.arc(r, r, r - ring / 2, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.lineWidth = ring;
  ctx.strokeStyle = "#ffffff";
  ctx.stroke();
  ctx.font = `${Math.round(px * 0.55)}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // A hair below the middle: emoji sit high on their baseline in every colour font.
  ctx.fillText(emoji, r, r + px * 0.04);
  return ctx.getImageData(0, 0, px, px);
}
